import { Box } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import * as Sentry from '@sentry/react';
import { LoadingSpinner } from '@shared/components/LoadingSpinner';
import { ReviewStep } from '@shared/domain/review';
import { useApi } from '@shared/services/api';
import { log } from '@shared/utils/log';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useContext, useEffect, useState } from 'react';
import { UserContext } from '@/shared/contexts/UserContext';
import { trackEvent } from '../../../third-party/ga';
import DeepDiveTrainingModal from '../../training/DeepDiveTrainingModal';
import { useReviewContext } from '../context/ReviewContext';
import BasicReviewForm from './review_forms/BasicReviewForm';
import EntryStrategyReviewForm from './review_forms/EntryStrategyReviewForm';
import EvaluationReviewForm from './review_forms/EvaluationReviewForm';
import LegalReviewForm from './review_forms/LegalReviewForm';
import NegotiationReviewForm from './review_forms/NegotiationReviewForm';
import PurchaseDetailsReviewForm from './review_forms/PurchaseDetailsReviewForm';
import RfpReviewForm from './review_forms/RfpReviewForm';
import ReviewDialogHeader from './ReviewDialogHeader';
import ReviewStepper from './ReviewStepper';

interface ReviewDialogProps {
  open: boolean;
  companyName?: string;
  companyId?: number;
  setOpen: (isOpen: boolean) => void;
  isAddCompany?: boolean;
  initialCompanyName?: string;
}

function ReviewDialog(props: ReviewDialogProps) {
  const { open, setOpen, companyName, companyId, isAddCompany, initialCompanyName } = props;
  const [rfp, setRfp] = useState(false);

  const api = useApi();

  const { user } = useContext(UserContext);
  const [showTrainingModal, setShowTrainingModal] = useState(false);
  const {
    handleFinishReview,
    handleServerError,
    fetchFilteredReviews,
    reviewId,
    reviewStep,
    setReviewStep,
    loading,
    setLoading,
    setLastStepSaved,
    lastStepSaved,
    resetReviewState,
    clearStepCache,
    setCurrentReview,
    setIsReviewDialogOpen,
  } = useReviewContext();

  useEffect(() => {
    if (open) {
      resetReviewState();
      setIsReviewDialogOpen(true);
    } else {
      setIsReviewDialogOpen(false);
    }
  }, [open, resetReviewState, setIsReviewDialogOpen]);

  useEffect(() => {
    if (open) {
      Sentry.setTag('review_dialog_opened', true);
      trackEvent({ eventName: 'review_dialog_opened' });
    }
  }, [open]);

  const needsDeepDiveTraining = open && user?.deepDiveTrainingComplete === false;

  useEffect(() => {
    if (needsDeepDiveTraining) setShowTrainingModal(true);
  }, [needsDeepDiveTraining]);

  const handleClose = () => {
    handleFinishReview(false);
    setOpen(false);
  };

  const shouldCompleteReview = (key: ReviewStep) => key === ReviewStep.RFP || (key === ReviewStep.NEGOTIATION && !rfp);

  const completeReview = () => {
    if (companyId) {
      fetchFilteredReviews({
        companyId,
        searchType: 'custom',
      });
    }

    handleFinishReview(true);
    setOpen(false);
  };

  const submitReviewForm = async (formFields: any, key: ReviewStep, isReviewDeepDive?: boolean) => {
    setLoading(true);
    if (!user?.idToken) {
      return;
    }

    const reviewInput = {
      ...formFields,
      key,
      reviewId,
    };

    try {
      const response: any = await api.post(`/review`, reviewInput);
      if (response?.error && response?.data?.code === 'DEEP_DIVE_TRAINING_REQUIRED') {
        setLoading(false);
        setShowTrainingModal(true);
        return;
      }

      const newLastStepSaved = Math.max(key, lastStepSaved || 0);
      setLastStepSaved(newLastStepSaved);
      clearStepCache(key);

      if (isReviewDeepDive && shouldCompleteReview(key)) {
        trackEvent({ eventName: 'deep_dive_review_completed' });
        await completeReview();
      } else {
        const nextStep = reviewStep + 1;
        setCurrentReview(null);
        setReviewStep(nextStep);
        setLoading(false);
      }
    } catch (error) {
      log.error('Error submitting review form:', error);
      Sentry.addBreadcrumb({
        message: 'Error submitting review form',
        level: 'error',
        data: {
          reviewInput,
          reviewId,
          reviewStep: key,
        },
      });
      handleServerError();
      setOpen(false);
    }
  };

  const formVariants = {
    initial: { opacity: 0, y: 8, scale: 0.99 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, y: -8, scale: 0.99 },
  } as const;

  const transition = { duration: 0.2, ease: 'easeOut' } as const;

  return (
    <Dialog open={open} onClose={handleClose} fullScreen data-testid="review-step-basic" className="review-dialog">
      <ReviewDialogHeader
        handleClose={handleClose}
        companyName={companyName || 'a New Company'}
        isDeepDive={!!reviewStep}
      />
      {loading && (
        <Box
          component={motion.div}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          aria-busy
          aria-live="polite"
          role="status"
          sx={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(255, 255, 255, 0.85)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2,
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            <LoadingSpinner variant="pulse" size="large" />
          </motion.div>
        </Box>
      )}

      <AnimatePresence mode="wait" initial={false}>
        <Box
          className="review-dialog-content"
          sx={{
            position: 'relative',
            zIndex: 1,
            minHeight: '60vh',
          }}
        >
          <motion.div
            key={reviewStep}
            variants={formVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={transition}
            style={{ position: 'relative', zIndex: 1 }}
          >
            {reviewStep !== ReviewStep.BASIC_REVIEW && (
              <ReviewStepper handleBack={setReviewStep} rfp={rfp} loading={loading} />
            )}
            {reviewStep === ReviewStep.BASIC_REVIEW && (
              <BasicReviewForm
                companyId={companyId || 0}
                companyName={companyName || initialCompanyName || 'New Company'}
                cancelReview={() => {
                  handleServerError();
                  setOpen(false);
                }}
                handleReviewSuccess={(showSnackbar: boolean, redirectPath?: string) => {
                  handleFinishReview(showSnackbar, redirectPath);
                  trackEvent({ eventName: 'basic_review_completed' });
                  setOpen(false);
                }}
                setRfp={setRfp}
                isAddCompany={isAddCompany}
              />
            )}
            {reviewStep === ReviewStep.ENTRY_STRATEGY && (
              <EntryStrategyReviewForm submitReviewForm={submitReviewForm} />
            )}
            {reviewStep === ReviewStep.PURCHASE_DETAILS && (
              <PurchaseDetailsReviewForm submitReviewForm={submitReviewForm} />
            )}
            {reviewStep === ReviewStep.EVALUATION && <EvaluationReviewForm submitReviewForm={submitReviewForm} />}
            {reviewStep === ReviewStep.LEGAL && <LegalReviewForm submitReviewForm={submitReviewForm} />}
            {reviewStep === ReviewStep.NEGOTIATION && (
              <NegotiationReviewForm submitReviewForm={submitReviewForm} rfp={rfp} />
            )}
            {reviewStep === ReviewStep.RFP && <RfpReviewForm submitReviewForm={submitReviewForm} />}
          </motion.div>
        </Box>
      </AnimatePresence>
      <DeepDiveTrainingModal
        open={showTrainingModal}
        onClose={() => {
          setShowTrainingModal(false);
          handleClose();
        }}
        onComplete={() => setShowTrainingModal(false)}
      />
    </Dialog>
  );
}

export default ReviewDialog;
